/**
 * HelpGuide — floating interactive helper that walks a user through a task.
 *
 * Deliberately NOT a chatbot: the user picks a task from a short list and the
 * guide then navigates them to the right page, spotlights the real control to
 * click, and advances itself when it observes the real work happening.
 *
 * Mounted once at the app root inside BrowserRouter but outside <Routes>
 * (App.jsx), so the route changes it performs never unmount it. Guide state is
 * in-memory only — a reload silently returns to the idle button, by design
 * (Decision 4: a restored mid-flow step would point at an anchor whose
 * surrounding state no longer exists).
 *
 * Full design: docs/INTERACTIVE_HELP_PLAN.md
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePortalVersion } from '../../contexts/PortalVersionContext.jsx';
import { getAvailableFlows, getFlow, allFlows } from '../../data/helpFlows/index.js';
import {
    buildHelpContext, MIN_ROLE_WEIGHT, HELP_OPEN_EVENT, emitHelpActive,
} from './helpAvailability.js';
import useAnchor, { countAnchors } from './useAnchor.js';
import { subscribe, isSatisfiedNow, isManual } from './detectors.js';
import SpotlightOverlay from './SpotlightOverlay.jsx';
import HelpPanel from './HelpPanel.jsx';
import useDock from './useDock.js';
import useSpotlightTimers from './useSpotlightTimers.js';
import useContextRegression from './useContextRegression.js';
import './help-guide.css';

const HelpGuide = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { portalVersion } = usePortalVersion();

    /* Re-read session on `session:updated` (session.js) so a campus switch can
       flip flow availability — and therefore whether the guide renders at all. */
    const [sessionTick, setSessionTick] = useState(0);
    useEffect(() => {
        const handler = () => setSessionTick((n) => n + 1);
        window.addEventListener('session:updated', handler);
        return () => window.removeEventListener('session:updated', handler);
    }, []);

    const ctx = useMemo(
        () => buildHelpContext(portalVersion),
        // sessionTick is the invalidation signal, not a value we read.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [portalVersion, sessionTick],
    );

    const flows = useMemo(() => getAvailableFlows(ctx), [ctx]);

    // ---- guide state ------------------------------------------------------
    const [pickerOpen, setPickerOpen] = useState(false);
    const [activeFlowId, setActiveFlowId] = useState(null);
    const [stepIndex, setStepIndex] = useState(0);
    const [finished, setFinished] = useState(false);
    const [timedOut, setTimedOut] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    /* What the user is *reading*, kept apart from what the engine is *watching*.
       null means "follow the active step". Selecting an upcoming step previews it
       without moving the engine — jumping the engine would break the detector
       chain and let a flow complete out of order. */
    const [selectedOverride, setSelectedOverride] = useState(null);

    /* Show me is opt-in and transient (see SpotlightOverlay). */
    const [spotlightOn, setSpotlightOn] = useState(false);

    const flow = activeFlowId ? getFlow(activeFlowId) : null;
    const steps = useMemo(
        () => (flow?.steps || []).filter((s) => (s.when ? s.when(ctx) : true)),
        [flow, ctx],
    );

    const activeIndex = steps.length ? Math.min(stepIndex, steps.length - 1) : 0;
    const step = !finished && steps.length ? steps[activeIndex] : null;
    const selectedIndex = selectedOverride != null && selectedOverride < steps.length
        ? selectedOverride
        : activeIndex;

    /* The anchor is only tracked while the spotlight is up — no idle rAF loop or
       MutationObserver when nobody asked to be shown anything. */
    const { rect, status: anchorStatus } = useAnchor(step?.anchor || null, spotlightOn && Boolean(step));

    const exit = useCallback(() => {
        setActiveFlowId(null);
        setStepIndex(0);
        setFinished(false);
        setTimedOut(false);
        setPickerOpen(false);
        setCollapsed(false);
        setSelectedOverride(null);
        setSpotlightOn(false);
    }, []);

    const advance = useCallback(() => {
        setTimedOut(false);
        setSpotlightOn(false);      // a completed step's highlight is stale
        setSelectedOverride(null);  // follow the engine to the next step
        setStepIndex((i) => {
            if (i + 1 >= steps.length) {
                setFinished(true);
                return i;
            }
            return i + 1;
        });
    }, [steps.length]);

    const selectStep = useCallback((i) => {
        setSelectedOverride(i);
        // Previewing another step shouldn't leave the active step's ring behind.
        setSpotlightOn(false);
    }, []);

    const startFlow = useCallback((f) => {
        setActiveFlowId(f.id);
        setStepIndex(0);
        setFinished(false);
        setTimedOut(false);
        setPickerOpen(false);
        setCollapsed(false);
        setSelectedOverride(null);
        setSpotlightOn(false);

        /* Dev-only anchor audit (§5.3): warn loudly if a flow references an id
           that exists nowhere, which is how anchor drift shows up. Delayed so the
           target route has a chance to mount. */
        if (import.meta.env.DEV) {
            setTimeout(() => {
                f.steps.forEach((s) => {
                    if (s.anchor && countAnchors(s.anchor) === 0) {
                        // eslint-disable-next-line no-console
                        console.warn(
                            `[HelpGuide] anchor "${s.anchor}" (flow "${f.id}", step "${s.id}") matched no elements. `
                            + 'Check the inventory in docs/INTERACTIVE_HELP_PLAN.md §5.2.',
                        );
                    }
                });
            }, 1500);
        }
    }, []);

    // ---- navigate for the step -------------------------------------------
    /* Auto-navigation happens once per step. Re-navigating on every pathname
       change would trap the user on the page: they could never click away, and a
       step whose completion IS a navigation (move-out step 1) would be undone the
       moment it succeeded. */
    const navigatedForRef = useRef(null);
    useEffect(() => {
        if (!step?.route?.navigate) return;
        const key = `${activeFlowId}:${step.id}`;
        if (navigatedForRef.current === key) return;
        navigatedForRef.current = key;
        if (!location.pathname.startsWith(step.route.path)) {
            navigate(step.route.path);
        }
    }, [step, activeFlowId, navigate, location.pathname]);

    // ---- leave-page recovery ---------------------------------------------
    /**
     * Has the user wandered off the page this step lives on?
     *
     * Deliberately advisory, not corrective: the guide offers "Take me back"
     * rather than yanking the route, because an unrequested navigation can
     * discard whatever the user went to do. A step whose own completion is a
     * route change (move-out step 1) is excluded — its detector matches a
     * *different* path than `route.path`, so being "off route" is success, not a
     * wrong turn.
     */
    const stepRoutePath = step?.route?.path || null;
    const completesByRoute = step?.done?.type === 'route';
    const offRoute = Boolean(
        stepRoutePath
        && !completesByRoute
        && !location.pathname.startsWith(stepRoutePath),
    );

    /* Keep the panel clear of any right-side drawer — that's where every submit
       button in this app lives. */
    const dockStyle = useDock(Boolean(activeFlowId));

    const returnToStepRoute = useCallback(() => {
        if (stepRoutePath) navigate(stepRoutePath);
    }, [navigate, stepRoutePath]);

    // ---- completion detection --------------------------------------------
    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;

    useEffect(() => {
        if (!step || finished) return undefined;

        /* Guard against a detector that is already satisfied the instant the step
           opens — otherwise a `dom` step whose target dialog is still on screen
           from the previous step would skip instantly. */
        if (isSatisfiedNow(step.done, { pathname: pathnameRef.current })) {
            // Defer so we don't setState during render/commit of this effect pass.
            const t = setTimeout(advance, 0);
            return () => clearTimeout(t);
        }

        const unsub = subscribe(step.done, { getPathname: () => pathnameRef.current }, advance);
        return unsub;
        // `advance` identity changes with steps.length only.
    }, [step, finished, advance]);

    // ---- context regression ----------------------------------------------
    /* Backing out of a transient container (row menu, dialog) returns the guide to
       the step that re-opens it. Completion always wins the race — see the hook. */
    useContextRegression({
        step,
        steps,
        finished,
        getPathname: () => pathnameRef.current,
        onRegress: (target) => {
            setStepIndex(target);
            setTimedOut(false);
        },
    });

    // ---- per-step timeout affordance -------------------------------------
    useEffect(() => {
        if (!step?.timeoutMs || finished) return undefined;
        setTimedOut(false);
        const t = setTimeout(() => setTimedOut(true), step.timeoutMs);
        return () => clearTimeout(t);
    }, [step, finished]);

    // ---- "Show me" lifecycle ---------------------------------------------
    /* Auto-show on arrival, then time out from when the target actually resolves.
       Both rules live in useSpotlightTimers so they can be verified on their own —
       this component can't be mounted outside a real session. */
    useSpotlightTimers({
        spotlightOn,
        setSpotlightOn,
        stepId: step?.id || null,
        flowId: activeFlowId,
        finished,
        collapsed,
        anchorFound: anchorStatus === 'found',
        hasStep: Boolean(step),
    });

    /* Acting on the thing the ring points at clears it immediately — no reason to
       keep pointing at something already clicked. */
    useEffect(() => {
        if (!spotlightOn || !step?.anchor) return undefined;
        /* Capture phase so we see the click even if the target stops propagation
           (several row buttons here do). Passive observation only — the click is
           never intercepted; the overlay is pointer-events:none. */
        const onClick = (e) => {
            const hit = e.target?.closest?.(`[data-help-id="${step.anchor}"]`);
            if (hit) setSpotlightOn(false);
        };
        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, [spotlightOn, step?.anchor]);

    // ---- launch from the nav sidebar --------------------------------------
    /* The trigger lives in NavigationV2, a separate tree, so it asks via a window
       event (see helpAvailability.js). Mid-flow the picker would be the wrong
       response — the panel is already the guide — so a click just brings a
       collapsed panel back instead of restarting. */
    useEffect(() => {
        const handler = () => {
            if (activeFlowId) {
                setCollapsed(false);
                return;
            }
            setPickerOpen((o) => !o);
        };
        window.addEventListener(HELP_OPEN_EVENT, handler);
        return () => window.removeEventListener(HELP_OPEN_EVENT, handler);
    }, [activeFlowId]);

    /* Escape closes the picker. It's visually modal once the backdrop is up, so
       the keyboard needs a way out that doesn't depend on finding the ✕. */
    useEffect(() => {
        if (!pickerOpen || activeFlowId) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setPickerOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [pickerOpen, activeFlowId]);

    /* Picker focus. This is the one place the guide DOES take focus: it's a
       deliberate modal moment (backdrop, Escape, aria-modal) and the trigger is
       far away in the nav, so leaving focus behind would strand keyboard users
       on a dimmed page. Per-step focus is a different matter and is deliberately
       never stolen — see the note on the panel below.

       Restoring to whatever was focused on open naturally returns the user to the
       Guided Help nav item without this component needing to know it exists. */
    const pickerRef = useRef(null);
    const restoreFocusRef = useRef(null);
    useEffect(() => {
        if (!pickerOpen || activeFlowId) return undefined;
        restoreFocusRef.current = document.activeElement;
        pickerRef.current?.focus();
        return () => {
            const el = restoreFocusRef.current;
            restoreFocusRef.current = null;
            /* Only restore if the element is still in the document and focus
               hasn't already moved somewhere meaningful (picking a task opens a
               drawer that manages its own focus). */
            if (el && el.isConnected && document.activeElement === document.body) {
                el.focus();
            }
        };
    }, [pickerOpen, activeFlowId]);

    /* Tell the nav trigger whether help is in use, so it can light up. "In use"
       covers both the picker being open and a flow running — from the nav's point
       of view those are the same thing: the helper is on screen.

       Emitting on unmount too, so the trigger cannot be left lit by a guide that
       is no longer there. */
    const helpActive = Boolean(pickerOpen || activeFlowId);
    useEffect(() => {
        emitHelpActive(helpActive);
        return () => emitHelpActive(false);
    }, [helpActive]);

    /* Announce step changes to screen readers. The spotlight ring is purely
       visual, so without this a non-sighted user gets no signal that the guide
       advanced. Lives here rather than in HelpPanel so collapsing to the pill
       doesn't unmount the live region mid-flow. */
    const announcement = useMemo(() => {
        if (!activeFlowId || !flow) return '';
        if (finished) return flow.success?.title || 'Guide complete';
        if (!step) return '';
        return `Step ${activeIndex + 1} of ${steps.length}: ${step.title}`;
    }, [activeFlowId, flow, finished, step, activeIndex, steps.length]);

    // ---- render gate (§7) -------------------------------------------------
    /* No DOM at all for ineligible users — not CSS-hidden. Mirrors
       isHelpAvailable(), which gates the nav trigger from the same rules. */
    if (portalVersion !== 'v2') return null;
    if (ctx.roleWeight <= MIN_ROLE_WEIGHT) return null;
    if (flows.length === 0) return null;

    return (
        <>
            {/* Task picker — launched from the nav sidebar's Guided Help item.
                Sits bottom-right where the panel will appear, so the picker→panel
                handoff happens in place. */}
            {/* Dim the content area so it's obvious something opened on the right —
                the trigger is all the way over in the nav. Clicking it dismisses,
                like every other backdrop in the app. */}
            {pickerOpen && !activeFlowId ? (
                <div
                    className="phg-backdrop"
                    onClick={() => setPickerOpen(false)}
                    aria-hidden="true"
                />
            ) : null}

            {/* Step announcements for screen readers. The ring is visual only, so
                this is the non-visual channel for "the guide moved on". */}
            {activeFlowId ? (
                <div className="phg-sr-only" role="status" aria-live="polite">{announcement}</div>
            ) : null}

            {pickerOpen && !activeFlowId ? (
                <div
                    className="phg-picker"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Help guide"
                    ref={pickerRef}
                    tabIndex={-1}
                >
                    <div className="phg-picker__head">
                        <span className="phg-picker__title">What can I help you complete?</span>
                        <button type="button" className="phg-iconbtn" onClick={() => setPickerOpen(false)} aria-label="Close">&#x2715;</button>
                    </div>
                    <div className="phg-picker__list">
                        {flows.map((f) => (
                            <button key={f.id} type="button" className="phg-task" onClick={() => startFlow(f)}>
                                <span className="phg-task__title">{f.title}</span>
                                <span className="phg-task__desc">{f.description}</span>
                                {/* Points out of the card: says "this starts something"
                                    rather than "this is a line of text". */}
                                <i className="fe fe-chevron-right phg-task__go" aria-hidden="true" />
                            </button>
                        ))}
                    </div>

                    {/* Absorbed from the old FAQ nav item, so "I need help" has one
                        destination: guided tasks for what the portal can walk you
                        through, the academy for everything else. */}
                    <div className="phg-picker__foot">
                        Need something else?{' '}
                        <a
                            className="phg-picker__academy"
                            href="https://www.palcare.com/academy/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Visit the PalCare Academy
                            <i className="fe fe-external-link" aria-hidden="true" />
                        </a>{' '}
                        for in-depth guides and videos.
                    </div>
                </div>
            ) : null}

            {/* Active flow: the panel is the guide — every step, always visible,
                with Exit in its header. */}
            {activeFlowId && flow ? (
                <HelpPanel
                    flow={flow}
                    steps={steps}
                    activeIndex={finished ? steps.length : activeIndex}
                    selectedIndex={selectedIndex}
                    finished={finished}
                    collapsed={collapsed}
                    spotlightOn={spotlightOn}
                    anchorStatus={anchorStatus}
                    timedOut={timedOut}
                    offRoute={offRoute}
                    isManualStep={Boolean(step && isManual(step.done))}
                    dockStyle={dockStyle}
                    onSelectStep={selectStep}
                    onToggleSpotlight={() => setSpotlightOn((v) => !v)}
                    onMarkDone={advance}
                    onReturn={returnToStepRoute}
                    onCollapse={() => setCollapsed(true)}
                    onExpand={() => setCollapsed(false)}
                    onExit={exit}
                    onRunAnother={() => { exit(); setPickerOpen(true); }}
                />
            ) : null}

            {/* On-demand highlight. Suppressed off-route (nothing meaningful to
                point at) and while collapsed (no panel to explain it). */}
            {step && !finished && spotlightOn && !offRoute && !collapsed ? (
                <SpotlightOverlay rect={rect} label={step.title} />
            ) : null}
        </>
    );
};

export default HelpGuide;

/* Exported for the dev harness / audit tooling. */
export { allFlows };
